import { useEffect, useMemo, useState } from "react";
import EditableForm from "./EditableForm";
import { fetchAPI } from "../../../utils/utils";
import { withWebDefaultMethod } from "../utils/autoPaymentMethod";

// How long the form waits for the person's saved answer before opening as a new one
const SAVED_ANSWER_TIMEOUT_MS = 8000;

function FillForm({ userId, isLogged, castellersInfo }) {
    const [formValues, setFormValues] = useState({});
    const [form, setForm] = useState(null);
    const [mote, setMote] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [loadedFormValuesScope, setLoadedFormValuesScope] = useState(null);
    // The person already answered this form: their answer keeps its own way to pay
    const [savedAnswer, setSavedAnswer] = useState(false);

    const evId = window.location.pathname.split('/')[2];
    const guestId = useMemo(() => `guest_${Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('')}`, []);
    const isLoggedUser = Number.isInteger(userId) && userId !== -1;
    const effectiveUserId = useMemo(
        () => (isLoggedUser ? String(userId) : guestId),
        [guestId, isLoggedUser, userId]
    );
    const formValuesScope = `${evId}:${effectiveUserId}`;

    useEffect(() => {
        let isMounted = true;
        const timeoutId = setTimeout(() => {
            if (!isMounted) return;
            setLoadError(true);
        }, 10000);

        setForm(null);
        setLoadError(false);
        setLoadedFormValuesScope(null);
        setSavedAnswer(false);

        fetchAPI(`/load_form/${evId}`, (data) => {
            if (!isMounted) return;
            clearTimeout(timeoutId);
            setForm(data);
            setLoadError(false);
        }, false);

        let savedAnswerTimeoutId;
        if (isLoggedUser) {
            let opened = false;
            const openAsNewAnswer = () => {
                opened = true;
                setFormValues({});
                setLoadedFormValuesScope(formValuesScope);
            };
            // A saved answer that doesn't come doesn't keep the form from opening
            savedAnswerTimeoutId = setTimeout(() => {
                if (isMounted) openAsNewAnswer();
            }, SAVED_ANSWER_TIMEOUT_MS);

            fetchAPI(`/is_form_submitted/${encodeURIComponent(effectiveUserId)}/${evId}`, (res) => {
                if (!isMounted) return;
                clearTimeout(savedAnswerTimeoutId);
                // Late, it still fills the form: sending it empty would replace the answer
                const saved = res?.success && String(res?.evId) === String(evId) ? res.response || {} : {};
                setFormValues(saved);
                setSavedAnswer(Object.keys(saved).length > 0);
                setLoadedFormValuesScope(formValuesScope);
            }, false, () => {
                // Without the saved answer the form still opens, as a new one
                if (!isMounted || opened) return;
                clearTimeout(savedAnswerTimeoutId);
                openAsNewAnswer();
            });
        } else {
            setFormValues({});
            setLoadedFormValuesScope(formValuesScope);
        }

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            clearTimeout(savedAnswerTimeoutId);
        };
    }, [effectiveUserId, evId, formValuesScope, isLoggedUser]);

    // Shown with its first choices already in place (the way to pay), so nothing
    // changes once it is on screen. Cleared ("Fes-ne una altra"), it is a new answer.
    const formValuesLoaded = loadedFormValuesScope === formValuesScope;
    const shownFormValues = useMemo(
        () => (form && formValuesLoaded
            ? withWebDefaultMethod(form, formValues, { savedAnswer: savedAnswer && Object.keys(formValues || {}).length > 0 })
            : formValues),
        [form, formValues, formValuesLoaded, savedAnswer]
    );

    useEffect(() => {
        if (isLogged) {
            setMote(castellersInfo?.[userId]?.mote || '');
        }
    }, [isLogged, userId, castellersInfo]);

    return (
        <div
            className="container"
            style={{
                width: '100%',
            }}
        >
            {
                loadError ? (
                    <div>
                        No s&apos;ha pogut carregar el formulari. Torna-ho a provar d&apos;aquí una estona.
                    </div>
                ) : form === null || !formValuesLoaded ? (
                    <div>
                        Carregant...
                    </div>
                ) : !form?.new ? (
                    <>
                        {/* <WelcomeUser
                            isLogged={isLogged}
                            userId={userId}
                            castellersInfo={castellersInfo}
                        /> */}
                        <EditableForm
                            formSubmitted={false}
                            formData={form}
                            formValues={shownFormValues}
                            formValuesLoaded={formValuesLoaded}
                            setFormValues={setFormValues}
                            evId={evId}
                            user={effectiveUserId}
                            mote={mote}
                            setMote={setMote}
                            castellersInfo={castellersInfo}
                            isLogged={isLogged}
                        />
                    </>
                ) : (
                    <div>
                        No hi ha formulari per aquest esdeveniment.
                    </div>
                )
            }
        </div>
    );
}

export default FillForm;
